import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { PaymentsService } from '../../services/payments.service';
import { PrintService } from '../../services/print.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { CollectionSheetData } from '../../shared/collection-sheet/collection-sheet.component';

/**
 * A/R Collection Tracker — the collections worklist.
 *
 * Every customer carrying an open balance, aged into buckets, sitting next to
 * the last collection contact and the next follow-up. Built to be worked top to
 * bottom: filter to what's due today, drill a customer, log the call, move on.
 *
 * Aging is live off `debtortrans`; the contact trail comes from
 * `ar_collection_notes` (migrations/ar_collection_notes.sql). On a database
 * where that migration hasn't been run the screen still shows aging.
 */
@Component({
	selector: 'app-collection-tracker',
	templateUrl: './collection-tracker.component.html',
	styleUrls: ['./collection-tracker.component.scss']
})
export class CollectionTrackerComponent implements OnInit {
	/** Spinner only on the very first load. */
	loading = false;
	/** A refresh is in flight; rows stay on screen. */
	refreshing = false;
	installed = true;
	installMessage = '';

	rows: any[] = [];
	summary: any = null;

	filters = {
		bucket: '',
		followup: '',
		salesperson: '',
		custtype: [] as string[],
		brand: '',
		// The worklist opens on live engagements — accounts somebody is already
		// working — rather than every aged account in the ledger. 'All accounts' is
		// one click away, and a customer search ignores this entirely.
		notes: 'with',
		min_balance: 0.01,
		search: '',
	};

	noteFilters = [
		{ value: 'with', label: 'Has notes', hint: 'Accounts someone is already working' },
		{ value: '', label: 'All accounts', hint: 'Every account with an open balance' },
		{ value: 'without', label: 'No notes yet', hint: 'Aged, but nobody has logged a contact' },
	];

	/** Customer types and brands that actually carry open A/R, from /options. */
	custtypes: any[] = [];
	brandOptions: any[] = [];

	/** The customer-type picker's panel; it is a set, so it can't be a <select>. */
	typeMenuOpen = false;
	@ViewChild('typeMenu') typeMenuRef: ElementRef;

	buckets = [
		{ value: '', label: 'All' },
		{ value: 'current', label: 'Current' },
		{ value: '1-30', label: '1-30' },
		{ value: '31-60', label: '31-60' },
		{ value: '61-90', label: '61-90' },
		{ value: '90plus', label: '90+' },
	];

	// 'Never contacted' / 'Contacted before' used to live here too; they are the
	// has-notes chips now, and leaving both would let you set two controls that
	// contradict each other and return nothing.
	followupFilters = [
		{ value: '', label: 'Everyone' },
		{ value: 'due', label: 'Follow-up due' },
		{ value: 'broken', label: 'Broken promises' },
		{ value: 'promised', label: 'Has open promise' },
	];

	/** Readable outcome labels — the row must not print raw codes like "promise". */
	outcomeLabels: Record<string, string> = {
		promise: 'Promised to pay',
		partial: 'Partial promise',
		plan: 'Payment plan',
		paid: 'Says paid',
		dispute: 'Disputes it',
		no_answer: 'No answer',
		message: 'Left message',
		refused: 'Refused',
		escalated: 'Escalated',
		other: 'Other',
	};

	/** Non-null while the collections flyout is open. */
	sheet: CollectionSheetData | null = null;

	/** Active sort column; '' keeps the server's own ordering (90+ then balance). */
	sort = '';
	dir: 'asc' | 'desc' = 'desc';

	/** The worklist table, cloned for XLS/PDF export. */
	@ViewChild('printTable') printTableRef: ElementRef;
	exporting = false;

	constructor(
		private paymentsService: PaymentsService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
	) {}

	ngOnInit(): void {
		this.load();
		this.loadOptions();
	}

	/**
	 * The two pickers, loaded once. They list what's in open A/R rather than the
	 * whole customer-type and manufacturer masters, so every entry leads
	 * somewhere.
	 */
	loadOptions(): void {
		this.paymentsService.getCollectionFilterOptions().subscribe((r: any) => {
			if (!r || !r.success) { return; }
			this.custtypes = r.custtypes || [];
			this.brandOptions = r.brands || [];
		});
	}

	get today(): string {
		return new Date().toISOString().substring(0, 10);
	}

	/**
	 * `silent` keeps the tiles and the worklist on screen while the request runs.
	 * Closing the flyout, changing a filter, or hitting Refresh must not blank the
	 * page and throw you back to the top of a 300-row list.
	 */
	load(silent = false) {
		const keepScroll = silent ? window.scrollY : null;
		if (silent) {
			this.refreshing = true;
		} else {
			this.loading = true;
		}
		// The set goes over the wire as a CSV; everything else passes straight through.
		const params = { ...this.filters, custtype: this.filters.custtype.join(',') };
		this.paymentsService.getCollectionTracker(params).subscribe((r: any) => {
			this.loading = false;
			this.refreshing = false;
			if (keepScroll !== null) {
				setTimeout(() => { window.scrollTo({ top: keepScroll }); });
			}
			if (!r || !r.success) {
				this.globalSearchService.showNotification(
					(r && r.message) || 'Could not load the collection tracker', 'danger', 'bottom', 'left'
				);
				return;
			}
			this.installed = !!r.installed;
			this.installMessage = r.installed ? '' : (r.message || '');
			this.rows = r.rows || [];
			this.summary = r.summary || null;
		});
	}

	setBucket(value: string) {
		this.filters.bucket = value;
		this.load(true);
	}

	setNotes(value: string) {
		this.filters.notes = value;
		this.load(true);
	}

	/**
	 * A customer search overrides has-notes, server-side. Searching is what you do
	 * when you're about to open an account for the first time, and "no results
	 * because nobody has called them yet" is a dead end. Mirrored here so the chips
	 * read as inactive instead of lying about what's on screen.
	 */
	get notesOverridden(): boolean {
		return !!this.filters.search && this.filters.notes !== '';
	}

	resetFilters() {
		this.filters = {
			bucket: '', followup: '', salesperson: '', custtype: [], brand: '',
			notes: 'with', min_balance: 0.01, search: '',
		};
		this.typeMenuOpen = false;
		this.load(true);
	}

	// -- customer type (multi-select) --------------------------------------
	// A set, not a single pick: collections works "every GM program account" or
	// "government + utility", not one type at a time. Native <select multiple> is
	// unusable at this size, so the panel is a custom one — same field furniture as
	// the rest of the bar, checkbox rows carrying each type's customer count and
	// open balance so you can see what you're about to filter to.

	toggleTypeMenu(): void {
		this.typeMenuOpen = !this.typeMenuOpen;
	}

	isTypeOn(typeid: any): boolean {
		return this.filters.custtype.indexOf(String(typeid)) >= 0;
	}

	toggleType(typeid: any): void {
		const id = String(typeid);
		const i = this.filters.custtype.indexOf(id);
		if (i >= 0) {
			this.filters.custtype.splice(i, 1);
		} else {
			this.filters.custtype.push(id);
		}
		this.load(true);
	}

	selectAllTypes(): void {
		this.filters.custtype = this.custtypes.map(t => String(t.typeid));
		this.load(true);
	}

	/** Empty set = every type, so clearing is the same as "All types". */
	clearTypes(): void {
		if (!this.filters.custtype.length) { return; }
		this.filters.custtype = [];
		this.load(true);
	}

	/** The chosen types as rows, in picker order, for the trigger and the export. */
	get selectedTypes(): any[] {
		return this.custtypes.filter(t => this.isTypeOn(t.typeid));
	}

	/** Click anywhere else closes the panel — it isn't a modal. */
	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (!this.typeMenuOpen || !this.typeMenuRef) { return; }
		if (!this.typeMenuRef.nativeElement.contains(event.target)) {
			this.typeMenuOpen = false;
		}
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		this.typeMenuOpen = false;
	}

	// -- brand mix ---------------------------------------------------------
	// The server sends three parallel pipe/comma lists (names, ids, estimated
	// open dollars) in one order, biggest first. Zipped here rather than shipped
	// as objects so a customer with forty brands doesn't bloat every row.

	brandMix(row: any): Array<{ id: string, name: string, amount: number }> {
		if (!row || !row.brands) { return []; }
		const names = String(row.brands).split('|');
		const ids = String(row.brand_ids || '').split(',');
		const amts = String(row.brand_amts || '').split(',');
		return names.map((name, i) => ({
			id: ids[i] || '',
			name: name,
			amount: Number(amts[i] || 0),
		}));
	}

	/** The two or three worth printing in the cell. */
	topBrands(row: any): Array<{ id: string, name: string, amount: number }> {
		return this.brandMix(row).slice(0, 2);
	}

	/** How many brands the cell had to leave out. */
	moreBrands(row: any): number {
		return Math.max(0, this.brandMix(row).length - 2);
	}

	/** Full mix for the hover, so nothing is hidden — just folded. */
	brandTooltip(row: any): string {
		return this.brandMix(row)
			.map(b => b.name + ' ' + this.money(b.amount))
			.join('\n');
	}

	/** Highlight the brand being filtered on — that's why the row is here. */
	brandFiltered(brand: { id: string }): boolean {
		return !!this.filters.brand && String(brand.id) === String(this.filters.brand);
	}

	/**
	 * Open the collections flyout for one account. It carries the collision
	 * warning, the payment activity, and the quick-log form, so the worklist
	 * stays a worklist instead of growing an inline panel.
	 */
	open(row: any) {
		// The whole filtered list rides along so the flyout can act as a call sheet.
		this.sheet = {
			debtorno: row.debtorno,
			queue: this.rows.map(r => ({ debtorno: r.debtorno, name: r.name })),
			index: this.rows.indexOf(row),
		};
	}

	onSheetClosed(wrote: boolean) {
		this.sheet = null;
		if (wrote) {
			this.load(true);
		}
	}

	// -- exports -----------------------------------------------------------
	// Same shape as the other feature-surface reports: clone the rendered table,
	// strip the interactive furniture, hand the HTML to the print service.

	get hasExportableRows(): boolean {
		return this.rows.length > 0;
	}

	private get exportTitle(): string {
		return 'A/R Collection Tracker';
	}

	private get exportSubtitle(): string {
		const bits: string[] = [];
		const bucket = this.buckets.find(b => b.value === this.filters.bucket);
		if (bucket && bucket.value) { bits.push('Bucket ' + bucket.label); }
		const state = this.followupFilters.find(f => f.value === this.filters.followup);
		if (state && state.value) { bits.push(state.label); }
		if (this.filters.salesperson) { bits.push('Rep ' + this.filters.salesperson); }
		if (this.filters.custtype.length) {
			bits.push(this.selectedTypes.map(t => t.typename).join(' + '));
		}
		if (this.filters.notes && !this.notesOverridden) {
			const n = this.noteFilters.find(f => f.value === this.filters.notes);
			if (n) { bits.push(n.label); }
		}
		const br = this.brandOptions.find(b => String(b.manufacturer_id) === String(this.filters.brand));
		if (br) { bits.push('Brand ' + br.name); }
		if (this.filters.search) { bits.push('Match "' + this.filters.search + '"'); }
		if (this.summary) {
			bits.push(this.summary.customers + ' customers');
			bits.push('Open ' + this.money(this.summary.balance));
			bits.push('Past due ' + this.money(this.summary.overdue));
		}
		return bits.join(' · ');
	}

	money(n: any): string {
		return '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	private get exportFilename(): string {
		return 'ar-collection-tracker';
	}

	/** Strip icons and controls so the export is data, not chrome. */
	private buildExportHtml(): string {
		const clone = this.printTableRef.nativeElement.cloneNode(true) as HTMLElement;
		clone.querySelectorAll('.donotprint, .noprint').forEach((n: Element) => n.remove());
		clone.querySelectorAll('mat-icon, .mat-icon, i.material-icons').forEach((n: Element) => n.remove());
		clone.querySelectorAll('button').forEach((b: Element) => {
			const text = (b.textContent || '').replace(/\s+/g, ' ').trim();
			b.replaceWith(clone.ownerDocument.createTextNode(text));
		});
		return clone.innerHTML;
	}

	exportXls(): void {
		if (!this.printTableRef) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle,
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	exportPdf(): void {
		if (!this.printTableRef) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle,
			// Twelve columns never fit portrait.
			landscape: true,
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	// -- sorting -----------------------------------------------------------
	// Client-side: the rows are already here, so sorting is instant and doesn't
	// cost a round trip or your scroll position.

	sortBy(key: string): void {
		if (this.sort === key) {
			this.dir = this.dir === 'desc' ? 'asc' : 'desc';
		} else {
			this.sort = key;
			// Money and ageing are most useful biggest-first; names read A-Z.
			this.dir = this.textColumns.indexOf(key) >= 0 ? 'asc' : 'desc';
		}
	}

	sortIcon(key: string): string {
		if (this.sort !== key) { return ''; }
		return this.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	private textColumns = ['name', 'salesmanname', 'paymentterms', 'debtorno', 'customertype'];

	get sortedRows(): any[] {
		if (!this.sort) { return this.rows; }
		const key = this.sort;
		const factor = this.dir === 'asc' ? 1 : -1;
		const isText = this.textColumns.indexOf(key) >= 0;

		// Copy first — sorting this.rows in place would fight trackBy and make the
		// list jump while you're reading it.
		return this.rows.slice().sort((a, b) => {
			const av = a[key];
			const bv = b[key];
			// Blanks always sink, whichever way the column is pointing.
			const aEmpty = av === null || av === undefined || av === '';
			const bEmpty = bv === null || bv === undefined || bv === '';
			if (aEmpty && bEmpty) { return 0; }
			if (aEmpty) { return 1; }
			if (bEmpty) { return -1; }
			if (isText) {
				return String(av).localeCompare(String(bv)) * factor;
			}
			if (key === 'last_contact_date') {
				return (String(av) < String(bv) ? -1 : String(av) > String(bv) ? 1 : 0) * factor;
			}
			return (Number(av) - Number(bv)) * factor;
		});
	}

	trackRow(index: number, row: any) {
		return row.debtorno;
	}

	outcomeLabel(value: string): string {
		return this.outcomeLabels[value] || value || '';
	}

	/**
	 * What actually became of the last contact. The outcome is what the customer
	 * SAID; the status is how it turned out — so a promise later marked broken
	 * must read "broken", not "promise".
	 */
	lastStatusPill(row: any): 'settled' | 'partial' | 'failed' | 'neutral' {
		if (row.last_status === 'broken') { return 'failed'; }
		if (row.last_status === 'kept') { return 'settled'; }
		if (row.last_status === 'closed') { return 'neutral'; }
		if (row.last_promise_date && row.last_promise_date < this.today) { return 'failed'; }
		return 'partial';
	}

	lastStatusLabel(row: any): string {
		if (row.last_status === 'broken') { return 'broken'; }
		if (row.last_status === 'kept') { return 'kept'; }
		if (row.last_status === 'closed') { return 'closed'; }
		if (row.last_promise_date && row.last_promise_date < this.today) { return 'promise broken'; }
		return 'open';
	}

	/** `tel:` needs bare digits, not the spaces people type into the field. */
	telHref(phone: string): string {
		return 'tel:' + String(phone || '').replace(/[^\d+]/g, '');
	}

	/**
	 * "2h ago" hint. Age is computed by the DB (`last_contact_age_seconds`) —
	 * the server clock and the browser clock are in different zones, so doing
	 * this from a timestamp string would be off by the offset.
	 */
	touchedAgo(row: any): string {
		const secs = row.last_contact_age_seconds;
		if (secs === null || secs === undefined) { return ''; }
		const h = Math.max(0, Number(secs) / 3600);
		if (h < 1) { return Math.round(h * 60) + 'm ago'; }
		if (h < 24) { return Math.round(h) + 'h ago'; }
		return Math.round(h / 24) + 'd ago';
	}

	/** Someone worked this account in the last day — warn before double-calling. */
	touchedToday(row: any): boolean {
		const secs = row.last_contact_age_seconds;
		return secs !== null && secs !== undefined && Number(secs) < 86400;
	}

	/** Follow-up that has already come and gone, or lands today. */
	followupLate(row: any): boolean {
		return !!row.overdue_followup || (!!row.next_followup && row.next_followup <= this.today);
	}

	/** What collections should do about this account next, in one phrase. */
	nextAction(row: any): string {
		if (!this.installed) { return ''; }
		if (row.last_status === 'broken' || Number(row.broken_promises) > 0) { return 'Broken promise'; }
		if (row.overdue_followup) { return 'Follow-up overdue'; }
		if (row.next_followup && row.next_followup <= this.today) { return 'Follow up today'; }
		if (row.next_followup) { return 'Follow up ' + row.next_followup; }
		if (!row.note_count) { return 'Never contacted'; }
		return 'No follow-up set';
	}

	actionTone(row: any): 'settled' | 'partial' | 'failed' | 'neutral' {
		if (row.last_status === 'broken' || Number(row.broken_promises) > 0 || row.overdue_followup) { return 'failed'; }
		if (row.next_followup && row.next_followup <= this.today) { return 'partial'; }
		if (!row.note_count) { return 'neutral'; }
		return 'settled';
	}

	ageTone(row: any): 'settled' | 'partial' | 'failed' | 'neutral' {
		if (Number(row.b_90plus) > 0) { return 'failed'; }
		if (Number(row.b_61_90) > 0 || Number(row.b_31_60) > 0) { return 'partial'; }
		if (Number(row.b_1_30) > 0) { return 'neutral'; }
		return 'settled';
	}
}
